import { useEffect } from 'react';
import { useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { parseUnits, type Address } from 'viem';
import { TOKEN_ABI, ADAPTER_ABI, BSC_EID, EMPTY_BYTES } from '../constants';
import { padAddressToBytes32, truncateDecimals } from '../utils/formatters';

interface UseBridgeActionsParams {
  tokenAddress: Address | undefined;
  adapterAddress: Address | undefined;
  sourceChainId: number;
  userAddress: Address | undefined;
  decimals: number;
  refetchBalance: () => void;
  refetchAllowance: () => void;
  clearForms: () => void;
}

/**
 * Hook for bridge write operations
 * Handles token approval and bridge execution
 */
export const useBridgeActions = ({
  tokenAddress,
  adapterAddress,
  sourceChainId,
  userAddress,
  decimals,
  refetchBalance,
  refetchAllowance,
  clearForms,
}: UseBridgeActionsParams) => {

  // Token approval
  const {
    writeContract: writeApprove,
    data: approveHash,
    isPending: isApprovePending,
  } = useWriteContract();

  const { isLoading: isApproveLoading, isSuccess: isApproveSuccess } =
    useWaitForTransactionReceipt({ hash: approveHash });

  // Bridge send
  const {
    writeContract: writeBridge,
    data: bridgeHash,
    isPending: isBridgePending,
    error: bridgeError,
  } = useWriteContract();

  const { isLoading: isBridgeLoading, isSuccess: isBridgeSuccess } =
    useWaitForTransactionReceipt({ hash: bridgeHash });

  // Refetch allowance after successful approval
  useEffect(() => {
    if (isApproveSuccess) {
      refetchAllowance();
    }
  }, [isApproveSuccess, refetchAllowance]);

  // Refetch balance and clear form after successful bridge
  useEffect(() => {
    if (isBridgeSuccess) {
      refetchBalance();
      clearForms();
    }
  }, [isBridgeSuccess, refetchBalance, clearForms]);

  /**
   * Approve token spending for the bridge adapter
   * @param amount - Amount to approve (string)
   */
  const handleApprove = async (amount: string) => {
    if (!tokenAddress || !adapterAddress || !amount) return;

    // CRITICAL: Truncate to 6 decimals before parsing (bridge constraint)
    const truncatedAmount = truncateDecimals(amount, 6);
    const amountBigInt = parseUnits(truncatedAmount, decimals);

    writeApprove({
      address: tokenAddress,
      abi: TOKEN_ABI,
      functionName: 'approve',
      args: [adapterAddress, amountBigInt],
      chainId: sourceChainId,
    });
  };

  /**
   * Execute bridge transaction
   * @param amount - Amount to bridge (string)
   * @param nativeFeeAmount - Native fee from quote
   * @param lzTokenFeeAmount - LZ token fee from quote
   */
  const handleBridge = async (
    amount: string,
    nativeFeeAmount: bigint,
    lzTokenFeeAmount: bigint
  ) => {
    if (!adapterAddress || !userAddress || !amount || nativeFeeAmount === 0n) {
      console.error('Missing required parameters for bridge');
      return;
    }

    // CRITICAL: Truncate to 6 decimals before parsing (bridge constraint)
    const truncatedAmount = truncateDecimals(amount, 6);
    const amountBigInt = parseUnits(truncatedAmount, decimals);
    const paddedAddress = padAddressToBytes32(userAddress);

    // Build SendParam struct
    // Note: Using EMPTY_BYTES for extraOptions (gas limit will be set by LayerZero defaults)
    const sendParam = {
      dstEid: BSC_EID,
      to: paddedAddress,
      amountLD: amountBigInt,
      minAmountLD: amountBigInt,
      extraOptions: EMPTY_BYTES, // Use empty bytes - LayerZero will use default gas settings
      composeMsg: EMPTY_BYTES,
      oftCmd: EMPTY_BYTES,
    };

    // Build MessagingFee struct
    const messagingFee = {
      nativeFee: nativeFeeAmount,
      lzTokenFee: lzTokenFeeAmount,
    };

    writeBridge({
      address: adapterAddress,
      abi: ADAPTER_ABI,
      functionName: 'send',
      args: [sendParam, messagingFee, userAddress],
      value: nativeFeeAmount, // Send native fee with transaction
      chainId: sourceChainId,
    });
  };

  return {
    // Approval
    handleApprove,
    isApprovePending,
    isApproveLoading,
    isApproveSuccess,
    approveHash,

    // Bridging
    handleBridge,
    isBridgePending,
    isBridgeLoading,
    isBridgeSuccess,
    bridgeHash,
    bridgeError,
  };
};
